import { useRef, useState } from "react";
import { Building2, Target, ShieldCheck, Users, BadgeCheck, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import Captcha from "@/components/site/Captcha";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { useSEO } from "@/lib/seo";
import { HIRE_TALENT_DEFAULTS, pick } from "@/lib/cms-defaults";
import { getIcon } from "@/lib/icon-map";
import { toast } from "sonner";

const COMPANY_TYPES = ["Pharmaceutical", "Biotechnology", "Medical Devices", "CRO", "CDMO", "Nutraceutical", "API / Bulk Drugs", "Formulations", "Healthcare / Hospital", "Other"];
const COMPANY_SIZES = ["1 - 50", "51 - 200", "201 - 500", "501 - 1000", "1000+"];
const URGENCY = ["Immediate (within 2 weeks)", "Within a month", "Within a quarter", "Exploratory"];
const EXPERIENCE = ["0 - 2 years", "2 - 5 years", "5 - 8 years", "8 - 12 years", "12 - 18 years", "18+ years"];

const EMPTY = {
  // Employer info
  company: "", name: "", designation: "", email: "", phone: "",
  website: "", address: "", company_type: "", company_size: "",
  // Requirement info
  role_needed: "", department: "", headcount: 1, job_location: "",
  experience_required: "", salary_range: "", urgency: "", message: "",
};

export default function HireTalent() {
  useSEO({
    title: "Hire Pharma Talent",
    description: "Partner with PharmacoHires to hire pre-vetted pharma, biotech and medical-device professionals. Share your requirement and get a shortlist within a week.",
    pageKey: "hire-talent",
  });
  const { settings } = useSiteSettings();
  const cms = settings?.hire_talent_content || {};
  const t = (key) => pick(cms, HIRE_TALENT_DEFAULTS, key);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef(null);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSelect = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { captcha_token, captcha_answer } = captchaRef.current?.getValues?.() || {};
      await api.post("/enquiries", {
        type: "hire_talent",
        ...form,
        headcount: Number(form.headcount) || 1,
        captcha_token,
        captcha_answer,
      });
      toast.success(t("submit_success_toast"));
      setForm(EMPTY);
      captchaRef.current?.reset?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
      captchaRef.current?.reset?.();
    } finally {
      setSubmitting(false);
    }
  };

  const valueProps = [1, 2, 3, 4].map((n) => ({
    Icon: getIcon(t(`vp_${n}_icon`), "Target"),
    title: t(`vp_${n}_title`),
    body: t(`vp_${n}_body`),
  }));
  const trustBullets = [1, 2, 3, 4, 5].map((n) => t(`trust_bullet_${n}`)).filter(Boolean);

  return (
    <>
      <PageHero
        overline={t("hero_overline")}
        title={t("hero_title")}
        description={t("hero_description")}
      />

      {/* Value props */}
      <section className="section-pad bg-white">
        <div className="container-page">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {valueProps.map(({ Icon, title, body }, i) => (
              <Reveal key={i} variant="up" delay={i * 100}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all">
                  <div className="mb-4 inline-grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form + trust column */}
      <section className="section-pad bg-slate-50">
        <div className="container-page grid gap-10 md:grid-cols-12">
          {/* Left — trust rail */}
          <aside className="md:col-span-4">
            <div className="sticky top-24 rounded-2xl border border-primary/20 bg-white p-6 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary">
                <BadgeCheck className="h-4 w-4" /> {t("trust_overline")} {settings?.brand_left || "Pharmaco"}{settings?.brand_right || "Hires"}
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900">{t("trust_title")}</h3>
              <ul className="mt-5 space-y-3 text-sm text-slate-700">
                {trustBullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-secondary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="font-semibold text-slate-900">{t("trust_prefer_title")}</div>
                <div className="mt-1 text-slate-600">Email <a href={`mailto:${settings?.contact_email || ""}`} className="text-primary hover:underline">{settings?.contact_email}</a></div>
                <div className="text-slate-600">Call <span className="text-slate-900">{settings?.contact_phone}</span></div>
              </div>
            </div>
          </aside>

          {/* Right — form */}
          <div className="md:col-span-8">
            <form onSubmit={submit} className="space-y-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="hire-talent-form">
              {/* Section 1 — Employer information */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  <h2 className="font-heading text-lg font-semibold text-slate-900">{t("employer_section_title")}</h2>
                </div>
                <p className="mb-5 text-xs text-slate-500">{t("employer_section_helper")}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="ht-company">Company name*</Label>
                    <Input id="ht-company" required value={form.company} onChange={onChange("company")} placeholder="e.g. NovaCure Biosciences Pvt Ltd" data-testid="ht-company" />
                  </div>
                  <div>
                    <Label htmlFor="ht-website">Company website</Label>
                    <Input id="ht-website" value={form.website} onChange={onChange("website")} placeholder="https://" data-testid="ht-website" />
                  </div>
                  <div>
                    <Label>Company type</Label>
                    <Select value={form.company_type} onValueChange={onSelect("company_type")}>
                      <SelectTrigger data-testid="ht-company-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {COMPANY_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company size</Label>
                    <Select value={form.company_size} onValueChange={onSelect("company_size")}>
                      <SelectTrigger data-testid="ht-company-size"><SelectValue placeholder="Headcount range" /></SelectTrigger>
                      <SelectContent>
                        {COMPANY_SIZES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ht-name">Contact person*</Label>
                    <Input id="ht-name" required value={form.name} onChange={onChange("name")} placeholder="Your full name" data-testid="ht-name" />
                  </div>
                  <div>
                    <Label htmlFor="ht-designation">Designation</Label>
                    <Input id="ht-designation" value={form.designation} onChange={onChange("designation")} placeholder="e.g. Head of HR" data-testid="ht-designation" />
                  </div>
                  <div>
                    <Label htmlFor="ht-email">Work email*</Label>
                    <Input id="ht-email" type="email" required value={form.email} onChange={onChange("email")} placeholder="you@company.com" data-testid="ht-email" />
                  </div>
                  <div>
                    <Label htmlFor="ht-phone">Phone*</Label>
                    <Input id="ht-phone" required value={form.phone} onChange={onChange("phone")} placeholder="+91 …" data-testid="ht-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ht-address">Company address</Label>
                    <Textarea id="ht-address" rows={2} value={form.address} onChange={onChange("address")} placeholder="City, state, country" data-testid="ht-address" />
                  </div>
                </div>
              </div>

              {/* Section 2 — Requirement */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <Target className="h-4 w-4 text-secondary" />
                  <h2 className="font-heading text-lg font-semibold text-slate-900">{t("requirement_section_title")}</h2>
                </div>
                <p className="mb-5 text-xs text-slate-500">{t("requirement_section_helper")}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="ht-role">Role / position*</Label>
                    <Input id="ht-role" required value={form.role_needed} onChange={onChange("role_needed")} placeholder="e.g. QA Manager - Sterile Formulations" data-testid="ht-role" />
                  </div>
                  <div>
                    <Label htmlFor="ht-department">Department / function</Label>
                    <Input id="ht-department" value={form.department} onChange={onChange("department")} placeholder="e.g. Quality, R&D, Regulatory" data-testid="ht-department" />
                  </div>
                  <div>
                    <Label htmlFor="ht-headcount">Number of positions</Label>
                    <Input id="ht-headcount" type="number" min="1" value={form.headcount} onChange={onChange("headcount")} data-testid="ht-headcount" />
                  </div>
                  <div>
                    <Label htmlFor="ht-location">Job location</Label>
                    <Input id="ht-location" value={form.job_location} onChange={onChange("job_location")} placeholder="e.g. Hyderabad / Bengaluru / Remote" data-testid="ht-location" />
                  </div>
                  <div>
                    <Label>Experience required</Label>
                    <Select value={form.experience_required} onValueChange={onSelect("experience_required")}>
                      <SelectTrigger data-testid="ht-experience"><SelectValue placeholder="Select experience band" /></SelectTrigger>
                      <SelectContent>
                        {EXPERIENCE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ht-salary">Salary range (CTC)</Label>
                    <Input id="ht-salary" value={form.salary_range} onChange={onChange("salary_range")} placeholder="e.g. ₹18 – 24 LPA" data-testid="ht-salary" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Urgency</Label>
                    <Select value={form.urgency} onValueChange={onSelect("urgency")}>
                      <SelectTrigger data-testid="ht-urgency"><SelectValue placeholder="Pick a timeline" /></SelectTrigger>
                      <SelectContent>
                        {URGENCY.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ht-message">Job description / must-haves</Label>
                    <Textarea id="ht-message" rows={5} value={form.message} onChange={onChange("message")} placeholder="Paste the JD or list key must-haves, therapy area, certifications, etc." data-testid="ht-message" />
                  </div>
                </div>
              </div>

              <Captcha ref={captchaRef} testId="ht-captcha" />

              <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-6">
                <p className="text-xs text-slate-500">{t("submit_footer_note")}</p>
                <Button type="submit" size="lg" disabled={submitting} className="bg-primary shadow-lg shadow-primary/20 hover:bg-primary/90" data-testid="ht-submit">
                  {submitting ? "Sending…" : (<>{t("submit_button_text")} <ArrowRight className="ml-2 h-4 w-4" /></>)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
